import { useState } from 'react';
import type { PortalCard, CardContentType, CardSize } from '@me-get/shared';
import { validateUrl } from '@me-get/shared';

export interface CardFormData {
  cardNumber: number;
  title: string;
  description: string;
  contentType: CardContentType;
  htmlContent: string;
  linkUrl: string;
  iconOrImage: string;
  cardSize: CardSize;
  textColor: string;
  bgColor: string;
}

interface CardEditorProps {
  cards: PortalCard[];
  companyId: string | null;
  isShared: boolean;
  onSave: (data: CardFormData, id?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  loading?: boolean;
}

const emptyForm: CardFormData = {
  cardNumber: 1,
  title: '',
  description: '',
  contentType: 'service_link',
  htmlContent: '',
  linkUrl: '',
  iconOrImage: '',
  cardSize: 'M',
  textColor: '',
  bgColor: '',
};

const contentTypeLabels: Record<CardContentType, string> = {
  html: 'Hard-inserted HTML',
  service_link: 'Link to existing service',
  external_link: 'Link to external application',
};

export function CardEditor({ cards, companyId, isShared, onSave, onDelete, loading }: CardEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CardFormData>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  function validate(data: CardFormData): Record<string, string> {
    const errs: Record<string, string> = {};

    if (!Number.isInteger(data.cardNumber) || data.cardNumber < 1) {
      errs.cardNumber = 'Card number must be a positive integer';
    } else {
      // Check uniqueness within current cards (exclude the one being edited)
      const isDuplicate = cards.some(
        (c) => c.cardNumber === data.cardNumber && c.id !== editingId
      );
      if (isDuplicate) {
        errs.cardNumber = 'Card number is already in use for this company';
      }
    }

    if (!data.title.trim()) {
      errs.title = 'Title is required';
    }

    if (data.description.length > 300) {
      errs.description = 'Description must be at most 300 characters';
    }

    if (data.contentType === 'html') {
      if (!data.htmlContent.trim()) {
        errs.htmlContent = 'HTML content is required for this content type';
      }
    } else {
      // service_link or external_link
      if (!data.linkUrl.trim()) {
        errs.linkUrl = 'URL is required for link-type cards';
      } else if (data.contentType === 'external_link') {
        // Only validate full URL format for external_link; service_link can use relative paths
        const urlResult = validateUrl(data.linkUrl);
        if (!urlResult.valid) {
          errs.linkUrl = urlResult.errors[0];
        }
      }
    }

    return errs;
  }

  function handleEdit(card: PortalCard) {
    setEditingId(card.id);
    setFormData({
      cardNumber: card.cardNumber,
      title: card.title,
      description: card.description,
      contentType: card.contentType,
      htmlContent: card.htmlContent ?? '',
      linkUrl: card.linkUrl ?? '',
      iconOrImage: card.iconOrImage ?? '',
      cardSize: card.cardSize ?? 'M',
      textColor: card.textColor ?? '',
      bgColor: card.bgColor ?? '',
    });
    setErrors({});
    setShowForm(true);
  }

  function handleNew() {
    setEditingId(null);
    const nextNumber = cards.length > 0 ? Math.max(...cards.map((c) => c.cardNumber)) + 1 : 1;
    setFormData({ ...emptyForm, cardNumber: nextNumber });
    setErrors({});
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyForm);
    setErrors({});
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSaving(true);
    try {
      await onSave(formData, editingId ?? undefined);
      handleCancel();
    } finally {
      setSaving(false);
    }
  }

  function handleFieldChange(field: keyof CardFormData, value: string | number) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  const sortedCards = [...cards].sort((a, b) => a.cardNumber - b.cardNumber);

  return (
    <div className="card-editor">
      <div className="card-editor__header">
        <h3>{isShared ? 'Shared Cards' : 'Company Cards'}</h3>
        <button className="btn btn-primary" onClick={handleNew} disabled={loading || saving}>
          + Add Card
        </button>
      </div>

      {showForm && (
        <form className="card-editor__form" onSubmit={handleSubmit} aria-label="Card form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="card-number">Card Number *</label>
              <input
                id="card-number"
                type="number"
                min="1"
                value={formData.cardNumber}
                onChange={(e) => handleFieldChange('cardNumber', parseInt(e.target.value, 10) || 0)}
                aria-invalid={!!errors.cardNumber}
                aria-describedby={errors.cardNumber ? 'card-number-error' : undefined}
              />
              {errors.cardNumber && (
                <span id="card-number-error" className="form-error">{errors.cardNumber}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="card-content-type">Content Type *</label>
              <select
                id="card-content-type"
                value={formData.contentType}
                onChange={(e) => handleFieldChange('contentType', e.target.value as CardContentType)}
              >
                {(Object.keys(contentTypeLabels) as CardContentType[]).map((type) => (
                  <option key={type} value={type}>
                    {contentTypeLabels[type]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="card-title">Title *</label>
            <input
              id="card-title"
              type="text"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="Card title"
              aria-invalid={!!errors.title}
              aria-describedby={errors.title ? 'card-title-error' : undefined}
            />
            {errors.title && <span id="card-title-error" className="form-error">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="card-description">
              Description <span className="char-count">({formData.description.length}/300)</span>
            </label>
            <textarea
              id="card-description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Card description (max 300 characters)"
              rows={3}
              aria-invalid={!!errors.description}
              aria-describedby={errors.description ? 'card-desc-error' : undefined}
            />
            {errors.description && (
              <span id="card-desc-error" className="form-error">{errors.description}</span>
            )}
          </div>

          {formData.contentType === 'html' ? (
            <div className="form-group">
              <label htmlFor="card-html-content">HTML Content *</label>
              <textarea
                id="card-html-content"
                value={formData.htmlContent}
                onChange={(e) => handleFieldChange('htmlContent', e.target.value)}
                placeholder="Enter HTML content"
                rows={6}
                aria-invalid={!!errors.htmlContent}
                aria-describedby={errors.htmlContent ? 'card-html-error' : undefined}
              />
              {errors.htmlContent && (
                <span id="card-html-error" className="form-error">{errors.htmlContent}</span>
              )}
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="card-link-url">URL *</label>
              <input
                id="card-link-url"
                type="text"
                value={formData.linkUrl}
                onChange={(e) => handleFieldChange('linkUrl', e.target.value)}
                placeholder="https://example.com"
                aria-invalid={!!errors.linkUrl}
                aria-describedby={errors.linkUrl ? 'card-link-error' : undefined}
              />
              {errors.linkUrl && (
                <span id="card-link-error" className="form-error">{errors.linkUrl}</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="card-icon">Icon or Image URL (optional)</label>
            <input
              id="card-icon"
              type="text"
              value={formData.iconOrImage}
              onChange={(e) => handleFieldChange('iconOrImage', e.target.value)}
              placeholder="Icon name or image URL"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="card-size">Card Size</label>
              <select
                id="card-size"
                value={formData.cardSize}
                onChange={(e) => handleFieldChange('cardSize', e.target.value)}
              >
                <option value="P">P (Small)</option>
                <option value="M">M (Medium)</option>
                <option value="G">G (Large)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="card-text-color">Text Color (optional)</label>
              <input
                id="card-text-color"
                type="text"
                value={formData.textColor}
                onChange={(e) => handleFieldChange('textColor', e.target.value)}
                placeholder="#000000"
              />
            </div>

            <div className="form-group">
              <label htmlFor="card-bg-color">Background Color (optional)</label>
              <input
                id="card-bg-color"
                type="text"
                value={formData.bgColor}
                onChange={(e) => handleFieldChange('bgColor', e.target.value)}
                placeholder="#ffffff"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Card' : 'Create Card'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="loading-text">Loading cards...</p>
      ) : sortedCards.length === 0 ? (
        <p className="empty-text">No cards configured yet.</p>
      ) : (
        <table className="data-table" aria-label="Cards list">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Type</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedCards.map((card) => (
              <tr key={card.id}>
                <td>{card.cardNumber}</td>
                <td>{card.title}</td>
                <td>{contentTypeLabels[card.contentType]}</td>
                <td className="description-cell">
                  {card.description.length > 60
                    ? `${card.description.slice(0, 60)}...`
                    : card.description}
                </td>
                <td className="actions-cell">
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(card)}>
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => onDelete(card.id)}
                    aria-label={`Delete card ${card.title}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
